import { useEffect, useState } from 'react';

export interface PublicBranding {
  readonly applicationName: string | null;
  readonly tagline: string | null;
  readonly primaryColor: string | null;
  readonly logoObjectKey: string | null;
}

/** Shown until the tenant's own name arrives, and when a tenant has no branding row. */
export const DEFAULT_PORTAL_NAME = 'Vendor Portal';

/**
 * Tenant branding for the pre-login screens. Served unauthenticated at
 * /v1/public/branding/:tenantCode, so it works before a session exists.
 */
export function usePublicBranding(tenantCode: string): PublicBranding | null {
  const [branding, setBranding] = useState<PublicBranding | null>(null);

  useEffect(() => {
    const code = tenantCode.trim();
    if (!code) { setBranding(null); return; }

    let cancelled = false;
    const ctrl = new AbortController();

    void (async () => {
      try {
        const res = await fetch(`/public/branding/${encodeURIComponent(code)}`, { signal: ctrl.signal });
        if (!res.ok) { if (!cancelled) setBranding(null); return; }
        const body = (await res.json()) as PublicBranding;
        if (!cancelled) setBranding(body);
      } catch {
        // An unknown tenant code or an unreachable API must not block sign-in.
        if (!cancelled) setBranding(null);
      }
    })();

    return () => { cancelled = true; ctrl.abort(); };
  }, [tenantCode]);

  return branding;
}

/** The tenant's application name, or the neutral fallback. */
export function portalName(branding: PublicBranding | null): string {
  const name = branding?.applicationName?.trim();
  return name && name.length > 0 ? name : DEFAULT_PORTAL_NAME;
}
